import { ShadowContainer } from '../../common/ShadowContainer';
import { TEXTS } from '../../../core/texts';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { BoxHoverCursor, FlexBox, FlexBoxFullWidth } from '../../common/boxes';
import { ChangeEvent, useCallback, useContext, useState } from 'react';
import { IAdminUpdateFreelancerProfile } from '../../../interfaces/adminUpdateFreelancerProfile';
import { Add, Check, Close, Edit } from '@mui/icons-material';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerService } from '../../../services/freelancer';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { Validator } from '../../../core/Validator';
import { FreelancerContext } from './FreelancerEntity';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../queryHooks/freelancer/useGetFreelancerEntity';
import { parseAxiosError } from '../../../services/axios';
import { useNotification } from '../../../+state/useNotification';

const Links = () => {
  const { user } = useContext(FreelancerContext);
  const { freelancerProfile: { linkedin } } = user;
  const queryClient = useQueryClient();
  const { openErrorToast, openSuccessToast } = useNotification();
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  const [state, setState] = useState<IAdminUpdateFreelancerProfile>({ linkedIn: user.freelancerProfile.linkedin });
  const [editMode, setEditMode] = useState(false);

  const mutation = useMutation({
    mutationFn: (update: IAdminUpdateFreelancerProfile) => FreelancerService.editProfile(user.id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [FREELANCER_ENTITY_QUERY_KEY] });
      setEditMode(false);
      setState({ linkedIn: data.freelancerProfile.linkedin });
      openSuccessToast('Successfully updated');
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const navigateToLinkedIn = useCallback(() => {
    const url = linkedin.match(/^https?:/) ? linkedin : '//' + linkedin;
    window.open(url);
  }, [linkedin]);

  const toggleEditMode = useCallback(() => setEditMode(prev => {
    if (prev) {
      setValidationErrors({});
      setState(prev => ({ ...prev, linkedIn: user.freelancerProfile.linkedin }));
    }
    return !prev;
  }), [user]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value.trim() }));
  }, []);

  const handleSubmit = useCallback(() => {
    const validError = Validator.validate(state, Validator.NAMES.PROFILE);
    if (validError) {
      return setValidationErrors(validError);
    }
    mutation.mutate(state);
  }, [state, mutation]);

  return (
    <ShadowContainer width={'100%'}>
      <FlexBoxFullWidth justifyContent={'space-between'} alignItems={'center'} mb={3}>
        <Typography variant={'h3'}>
          {TEXTS.LINKS}
        </Typography>
        {
          !editMode &&
          <Tooltip title={user.freelancerProfile.linkedin ? 'Edit' : 'Add'}>
            <IconButton color={'primary'} onClick={toggleEditMode}>
              {
                user.freelancerProfile.linkedin
                  ? <Edit/>
                  : <Add/>
              }
            </IconButton>
          </Tooltip>
        }

      </FlexBoxFullWidth>
      <FlexBoxFullWidth>
        {
          user.freelancerProfile.linkedin
            ? <BoxHoverCursor onClick={navigateToLinkedIn} width={{ mobile: 30, tablet: 40 }}>
              <img src={'/linkedin.svg'} style={{ width: '100%' }} alt={'linkedin svg'}/>
            </BoxHoverCursor>
            : <Typography>
              {TEXTS.NOT_UPLOADED}
            </Typography>
        }
      </FlexBoxFullWidth>
      {
        (linkedin || editMode) &&
        <FlexBoxFullWidth mt={2} alignItems={'center'} justifyContent={'space-between'}>
          <TextField value={state.linkedIn} name={'linkedIn'} label={'LinkedIn Url'} error={!!validationErrors.linkedIn}
                     helperText={validationErrors.linkedIn} onChange={handleInputChange} sx={{ width: '80%' }}
                     disabled={!editMode} variant={editMode ? 'outlined' : 'standard'}/>
          {
            editMode &&
            <Box>
              <FlexBox>
                <Tooltip title={TEXTS.CANCEL}>
                  <IconButton onClick={toggleEditMode} color={'error'}>
                    <Close/>
                  </IconButton>
                </Tooltip>
                <Tooltip title={TEXTS.SAVE}>
                  <IconButton color={'primary'} onClick={handleSubmit} disabled={linkedin === state.linkedIn}>
                    <Check/>
                  </IconButton>
                </Tooltip>
              </FlexBox>
            </Box>
          }
        </FlexBoxFullWidth>
      }

    </ShadowContainer>
  );
};

export default Links;